export type PortalAuthSource = 'platform_public' | 'tenant_oidc';

export interface PortalLoginAction {
  source: PortalAuthSource;
  label: string;
}

export interface PortalLoginOptions {
  host: string;
  mode: 'platform' | 'tenant';
  available: boolean;
  tenant?: {
    id: string;
    slug: string;
    displayName: string;
    brandingRef?: string | null;
  };
  primary: PortalLoginAction;
  fallback?: PortalLoginAction;
}

export interface StartPortalLoginInput {
  source: PortalAuthSource;
  returnPath?: string;
  register?: boolean;
  tenantSlug?: string;
}

export interface PortalLoginApi {
  resolveOptions(tenantSelectors?: readonly string[], signal?: AbortSignal): Promise<PortalLoginOptions>;
  start(input: StartPortalLoginInput, signal?: AbortSignal): Promise<{ redirectUrl: string }>;
}

export class PortalLoginApiError extends Error {
  constructor(public readonly status: number) {
    super(status === 404 || status === 503 ? 'Tenant login is currently unavailable.' : 'Login could not be started.');
    this.name = 'PortalLoginApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      'X-Request-ID': `portal_${crypto.randomUUID()}`,
      ...(init?.headers ?? {}),
    },
  });
  if (!response.ok) {
    throw new PortalLoginApiError(response.status);
  }
  return response.json() as Promise<T>;
}

export function buildPortalLoginOptionsPath(tenantSelectors: readonly string[] = []): string {
  const query = new URLSearchParams();
  tenantSelectors.forEach((tenant) => query.append('tenant', tenant));
  const encoded = query.toString();
  const suffix = encoded ? `?${encoded}` : '';
  return `/api/v1/auth/login-options${suffix}`;
}

export function buildPortalLoginStartPath(tenantSlug?: string): string {
  if (tenantSlug === undefined) return '/api/v1/auth/login/start';
  const query = new URLSearchParams({ tenant: tenantSlug });
  return `/api/v1/auth/login/start?${query.toString()}`;
}

export const portalLoginApi: PortalLoginApi = {
  resolveOptions: (tenantSelectors = [], signal) => request<PortalLoginOptions>(buildPortalLoginOptionsPath(tenantSelectors), { signal }),
  start: ({ tenantSlug, ...input }, signal) => request<{ redirectUrl: string }>(buildPortalLoginStartPath(tenantSlug), {
    method: 'POST',
    signal,
    body: JSON.stringify(input),
  }),
};
